import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import YSaatioPageContainer from '../YSaatioPageContainer';
import TerminationNoticeApproval from './TerminationNoticeApproval';
import Loader from '../../../components/basic/Loader';
import { useKennoTenant } from '../../../context/kennoTenantContext';
import { loadKennoNotifications, loadTerminationNotice, loadContract } from '../../../context/kennoTenantActions';
import { LOAD_STATES } from '../../../context/common';
import { KennoNotification, KennoNotificationCategory } from '../../../types/notifications';
import { ContractTerminationStatus, TerminationNotice } from './schema';
import { TerminationInstructions } from './TerminationInstructions';
import { YSaatioRoutes } from '../../../constants/routes';
import { scrollToTop } from '../../../utils';
import TerminationDisallowedNotification, { getTerminationNotificationType } from './TerminationDisallowedNotification';
import { NotificationType } from '../../../context/appContext';

/**
 * Check whether termination notice is pending approval from this tenant.
 * If termination notice is pending tenant approval and the the tenant has an
 * active notification for termination request approval, returns true.
 *
 */
const isTenantExpectedToApproveTerminationNotice = (
  terminationNotice: TerminationNotice,
  notifications: KennoNotification[]
): boolean => {
  if (terminationNotice.status !== ContractTerminationStatus.PENDING_TENANT_APPROVAL) {
    return false;
  }
  return notifications.some(
    (n) => n.category === KennoNotificationCategory.CONTRACT_TERMINATION_REQUEST_APPROVAL_NEEDED && !n.closedAt
  );
};

export const YSaatioTerminationHomePage = ({ history }: RouteComponentProps) => {
  const [{ terminationNotice, notifications, contract }, dispatch] = useKennoTenant();
  const [hasReadInstructions, setHasReadInstructions] = useState(false);
  const terminationDisallowedReason = contract.data?.terminationDisallowedReason;
  const notificationType = getTerminationNotificationType(terminationDisallowedReason);

  useEffect(() => {
    loadTerminationNotice(dispatch);
    loadKennoNotifications(dispatch);
    loadContract(dispatch);
  }, [dispatch]);

  const navigateToTerminationForm = () => {
    history.push(YSaatioRoutes.FORM);
    scrollToTop();
  };

  const setInstructionsRead = () => {
    setHasReadInstructions(true);
  };

  const redirectToStrongAuth = (url: string) => {
    window.location.href = url;
  };

  const renderComponent = () => {
    const { loadState, data } = terminationNotice;

    if (loadState === LOAD_STATES.BUSY || contract.loadState === LOAD_STATES.BUSY) {
      return <Loader padding="64px" />;
    }
    if (data) {
      const isPendingApproval = isTenantExpectedToApproveTerminationNotice(data, notifications.data || []);

      if (isPendingApproval && !hasReadInstructions) {
        return (
          <>
            {terminationDisallowedReason && (
              <TerminationDisallowedNotification kennoTerminationDisallowedReason={terminationDisallowedReason} />
            )}
            <TerminationInstructions
              afterSubmit={() => setInstructionsRead()}
              isContinueDisabled={notificationType === NotificationType.ERROR}
            />
          </>
        );
      }
      if (data.status !== ContractTerminationStatus.REJECTED || isPendingApproval) {
        return (
          <TerminationNoticeApproval
            terminationNotice={data}
            afterApproval={redirectToStrongAuth}
            tenantApprovalRequired={isPendingApproval}
          />
        );
      }
    }
    return (
      <>
        {terminationDisallowedReason && (
          <TerminationDisallowedNotification kennoTerminationDisallowedReason={terminationDisallowedReason} />
        )}
        <TerminationInstructions
          afterSubmit={() => navigateToTerminationForm()}
          isContinueDisabled={notificationType === NotificationType.ERROR}
        />
      </>
    );
  };
  return <YSaatioPageContainer history={history}>{renderComponent()}</YSaatioPageContainer>;
};
